/**
 * Traffic inspector API: SSE stream URL and REST for event/trace detail.
 * Uses the same base URL and API key as the March / command admin API.
 */

import type {
  TrafficInspectorEventDetail,
  TrafficInspectorEvent,
  TrafficInspectorNode,
  TrafficInspectorEdge,
} from '../types/trafficInspector';
import { MARCH_NODE_IDS, buildMarchTopology, type NodeHealthMap } from './marchTopology';
import { isValidTopology } from '../utils/trafficTopologyState';
import { getMarchHttpApiBase, getMarchRuntimeBearer, isMarchHttpApiConfigured } from './marchApiEnv';
import { fetchWithAuth } from './fetchWithAuth';
import { SESSION_EXPIRED_MESSAGE, STREAM_RECONNECTING_MESSAGE } from '../auth/tokenLifecycle';
import { readNamedSseStream } from './sseStreamReader';

async function safeJson<T = any>(res: Response): Promise<T> {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    const text = await res.text();
    throw new Error(`API returned an invalid JSON response. The endpoint may be temporarily unavailable or misconfigured. Expected JSON but got ${ct}: ${text.substring(0, 50)}...`);
  }
  try {
    return await res.json() as T;
  } catch (e) {
    throw new Error('API returned an invalid JSON response.');
  }
}


const getBase = (): string => getMarchHttpApiBase();
const getApiKey = (): string => (import.meta.env.VITE_COMMAND_API_KEY ?? '').trim();

const getBearer = (): string => getMarchRuntimeBearer();
const getMode = (): 'march' | 'command' | 'auto' => {
  const raw = (import.meta.env.VITE_API_MODE ?? 'auto').toString().trim().toLowerCase();
  if (raw === 'march' || raw === 'command') return raw;
  return 'auto';
};

export function getTrafficInspectorMode(): 'march' | 'command' | 'none' {
  if (!isMarchHttpApiConfigured()) return 'none';
  const mode = getMode();
  if (mode === 'march') return 'march';
  if (mode === 'command') return 'command';
  return import.meta.env.VITE_MARCH_API_URL ? 'march' : 'command';
}

function adminHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = getApiKey();
  if (key) h['X-API-Key'] = key;
  const bearer = getBearer();
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

/** Auth headers for admin SSE streams (no Content-Type). */
export function trafficStreamHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  const key = getApiKey();
  if (key) h['X-API-Key'] = key;
  const bearer = getBearer();
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

const TRAFFIC_STREAM_PATH = '/v1/admin/traffic/stream';

export function getTrafficStreamSsePath(): string {
  return TRAFFIC_STREAM_PATH;
}

export function resolveTrafficStreamUrl(): string | null {
  if (!isMarchHttpApiConfigured()) return null;
  const base = getBase();
  const absoluteBase = base.length === 0 ? window.location.origin : base;
  return `${absoluteBase.replace(/\/+$/, '')}${TRAFFIC_STREAM_PATH}`;
}

export type AdminTrafficStreamHandlers = {
  onOpen?: () => void;
  onTrafficPayload: (data: unknown) => void;
  onTopologyPayload: (data: unknown) => void;
  onMeta?: () => void;
  onStreamWarning?: (message: string) => void;
  onFetchError?: (status: number, message: string) => void;
  onDisconnected?: () => void;
  onReconnecting?: () => void;
};

/**
 * #113: max time to wait for the FIRST byte of stream signal (response headers
 * plus the backend's immediate `connected` event). On the plain-HTTP March
 * environments the browser buffers a streamed fetch to completion (#145): the
 * request neither errors nor yields, so without this deadline the connection
 * is held forever, the UI stays "connecting", and no fallback ever starts.
 * A healthy backend emits `connected` within ~1s of the request, so a stale
 * stream is distinguishable from a quiet one long before this fires.
 */
export const STREAM_FIRST_SIGNAL_TIMEOUT_MS = 10_000;

/**
 * #113-hardening: on a plain-HTTP origin, every streamed fetch is buffered by
 * the browser until it completes (#145) — no SSE event ever reaches the page,
 * so an automatic connection attempt is a guaranteed dead socket held for the
 * full STREAM_FIRST_SIGNAL_TIMEOUT_MS window for nothing. This is a structural
 * signal (the page's own URL scheme), not content/keyword matching, and lets
 * the caller skip the attempt entirely rather than start-then-time-out.
 */
export function isPlainHttpTrafficOrigin(): boolean {
  return typeof window !== 'undefined' && window.location.protocol === 'http:';
}

const TRAFFIC_EVENT_NAMES = new Set([
  'traffic',
  'traffic.event',
  'request.created',
  'request.updated',
  'request.completed',
]);

const META_EVENT_NAMES = new Set([
  'connected',
  'heartbeat',
  'stream.heartbeat',
  'health.updated',
]);

/**
 * Open the admin traffic SSE stream with Authorization header (never query tokens).
 * Returns an abort function to cancel the stream.
 */
export function connectAdminTrafficSseStream(handlers: AdminTrafficStreamHandlers): () => void {
  const controller = new AbortController();
  const url = resolveTrafficStreamUrl();
  if (!url) {
    handlers.onFetchError?.(0, 'Traffic stream URL not configured');
    handlers.onDisconnected?.();
    return () => undefined;
  }

  // #113 liveness deadline: if neither the response nor the backend's first
  // SSE event arrives in time (plain-HTTP buffering, #145), abort the held
  // connection so the browser gets its socket back and the caller can fall
  // back to history polling. `stale` marks the abort as OURS so the abort
  // handling below reports a disconnect instead of swallowing it silently
  // (a user-initiated abort via the returned cancel fn stays silent).
  let stale = false;
  let sawFirstSignal = false;
  const firstSignalTimer = setTimeout(() => {
    if (sawFirstSignal) return;
    stale = true;
    controller.abort();
  }, STREAM_FIRST_SIGNAL_TIMEOUT_MS);
  const markFirstSignal = () => {
    if (sawFirstSignal) return;
    sawFirstSignal = true;
    clearTimeout(firstSignalTimer);
  };

  void (async () => {
    try {
      const resp = await fetchWithAuth(url, {
        method: 'GET',
        extraHeaders: trafficStreamHeaders(),
        signal: controller.signal,
        onAuthRetry: () =>
          handlers.onReconnecting?.() ??
          handlers.onStreamWarning?.(STREAM_RECONNECTING_MESSAGE),
      });
      if (!resp.ok) {
        markFirstSignal();
        const text = await resp.text();
        const safeMessage =
          resp.status === 401
            ? SESSION_EXPIRED_MESSAGE
            : resp.status === 403
              ? 'You do not have permission to access this resource.'
              : text || resp.statusText;
        handlers.onFetchError?.(resp.status, safeMessage);
        handlers.onDisconnected?.();
        return;
      }
      if (!resp.body) {
        markFirstSignal();
        handlers.onFetchError?.(0, 'Stream response missing body');
        handlers.onDisconnected?.();
        return;
      }

      handlers.onOpen?.();

      await readNamedSseStream(resp, (eventName, data) => {
        // Response headers can arrive even when the event bytes are being
        // buffered upstream (#145), so liveness is only proven by a parsed
        // event — clear the deadline here, not at onOpen.
        markFirstSignal();
        if (controller.signal.aborted) return;

        if (eventName === 'topology.updated') {
          handlers.onMeta?.();
          handlers.onTopologyPayload(data);
          return;
        }

        if (eventName === 'stream_error') {
          handlers.onMeta?.();
          const payload = data as { metadata?: { message?: string } } | null;
          const msg = payload?.metadata?.message;
          handlers.onStreamWarning?.(
            msg ? `Traffic stream warning: ${msg}` : 'Traffic stream warning',
          );
          return;
        }

        if (eventName && META_EVENT_NAMES.has(eventName)) {
          handlers.onMeta?.();
          return;
        }

        if (eventName && TRAFFIC_EVENT_NAMES.has(eventName)) {
          handlers.onMeta?.();
          handlers.onTrafficPayload(data);
          return;
        }

        // Default SSE message (no event name) or unknown named events with payload.
        handlers.onMeta?.();
        handlers.onTrafficPayload(data);
      });

      if (!controller.signal.aborted) {
        handlers.onDisconnected?.();
      } else if (stale) {
        handlers.onStreamWarning?.(
          'Live traffic stream produced no events (plain-HTTP buffering); switching to periodic history refresh.',
        );
        handlers.onDisconnected?.();
      }
    } catch (error) {
      if (controller.signal.aborted) {
        if (stale) {
          handlers.onStreamWarning?.(
            'Live traffic stream produced no events (plain-HTTP buffering); switching to periodic history refresh.',
          );
          handlers.onDisconnected?.();
        }
        return;
      }
      handlers.onDisconnected?.();
    } finally {
      markFirstSignal();
    }
  })();

  return () => {
    markFirstSignal();
    controller.abort();
  };
}

export function getTrafficInspectorBaseUrl(): string {
  return getBase();
}

export function getTrafficInspectorHeaders(): Record<string, string> {
  return adminHeaders();
}

async function adminFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const base = getBase();
  if (!isMarchHttpApiConfigured()) throw new Error('Command API URL not configured');
  const res = await fetchWithAuth(`${base}${path}`, {
    ...options,
    extraHeaders: {
      ...adminHeaders(),
      ...(options?.headers as Record<string, string>),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    if (res.status === 401) throw new Error('Invalid or missing API key');
    if (res.status === 403) throw new Error('Admin or auditor role required');
    throw new Error(text || `Request failed ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** WebSocket URL for live traffic stream. Auth must not use JWT query params. */
export function getTrafficStreamUrl(): string | null {
  const base = getBase();
  if (!isMarchHttpApiConfigured()) return null;
  if (getTrafficInspectorMode() === 'march') return null;
  const wsBase =
    base.length === 0
      ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
      : base.replace(/^http/, 'ws');
  const url = new URL(`${wsBase}${TRAFFIC_STREAM_PATH}`);
  const key = getApiKey();
  if (key) url.searchParams.set('api_key', key);
  return url.toString();
}

/** Whether admin traffic SSE is available when the March HTTP API base is configured. */
export function getTrafficStreamSseUrl(): string | null {
  if (getTrafficInspectorMode() === 'none') return null;
  return resolveTrafficStreamUrl();
}

/** Backend report of how many Traffic Inspector clients are connected. Use to verify this tab is registered. */
export async function getTrafficStreamStatus(): Promise<{ connectedClients: number } | null> {
  if (getTrafficInspectorMode() === 'march') {
    return { connectedClients: 1 };
  }
  try {
    return await adminFetch<{ connectedClients: number }>('/v1/admin/traffic/status');
  } catch {
    return null;
  }
}

/**
 * Send a test POST /v1/commands so an event appears in the Request stream.
 * Uses the same backend URL as the WebSocket; if the event appears, the pipeline works.
 */
export async function sendTestCommand(): Promise<{ ok: boolean; message?: string }> {
  const base = getBase();
  if (!isMarchHttpApiConfigured()) return { ok: false, message: 'API URL not set' };
  if (getTrafficInspectorMode() === 'march') {
    try {
      const conversationId = `ti-${Date.now()}`;
      const accepted = await fetchWithAuth(`${base}/api/v1/chat/message`, {
        method: 'POST',
        extraHeaders: adminHeaders(),
        body: JSON.stringify({
          message: '/summary window=last_7_days',
          patient_id: 'p-dev-001',
          conversation_id: conversationId,
        }),
      });
      if (!accepted.ok) {
        const text = await accepted.text();
        return { ok: false, message: `March API ${accepted.status}: ${text.slice(0, 100)}` };
      }
      const streamResp = await fetchWithAuth(
        `${base}/api/v1/chat/stream?conversation_id=${encodeURIComponent(conversationId)}&patient_id=p-dev-001`,
        { method: 'GET', extraHeaders: adminHeaders() },
      );
      if (!streamResp.ok) {
        const text = await streamResp.text();
        return { ok: false, message: `March stream ${streamResp.status}: ${text.slice(0, 100)}` };
      }
      // #113: release the SSE connection — leaving the body unconsumed held a
      // browser socket open for the rest of the session on plain HTTP.
      void streamResp.body?.cancel().catch(() => undefined);
      return { ok: true, message: 'Sent test chat to March API. Topology edges updated from canonical March graph.' };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : 'Request failed' };
    }
  }
  try {
    const res = await fetchWithAuth(`${base}/v1/commands`, {
      method: 'POST',
      extraHeaders: adminHeaders(),
      body: JSON.stringify({ command: '/simulate', tenantId: 'default', patientId: 'p1' }),
    });
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, message: `Backend ${res.status}: ${text.slice(0, 80)}` };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : 'Request failed' };
  }
}

/** Fetch event detail by id (GET /v1/admin/traffic/events/:id). */
export async function getEventDetail(eventId: string): Promise<TrafficInspectorEventDetail | null> {
  try {
    return await adminFetch<TrafficInspectorEventDetail>(`/v1/admin/traffic/events/${encodeURIComponent(eventId)}`);
  } catch {
    return null;
  }
}

/** Fetch event detail by traceId when id lookup 404s (GET /v1/admin/traffic/events?traceId=xxx). */
export async function getEventDetailByTraceId(traceId: string): Promise<TrafficInspectorEventDetail | null> {
  try {
    return await adminFetch<TrafficInspectorEventDetail>(
      `/v1/admin/traffic/events?traceId=${encodeURIComponent(traceId)}`
    );
  } catch {
    return null;
  }
}

/** Fetch full trace by traceId (when backend implements GET /v1/admin/traffic/traces/:traceId). */
export async function getTrace(traceId: string): Promise<{ spans: Array<{ spanId: string; name: string; durationMs: number; status: string }> } | null> {
  try {
    return await adminFetch<{ spans: Array<{ spanId: string; name: string; durationMs: number; status: string }> }>(
      `/v1/admin/traffic/traces/${encodeURIComponent(traceId)}`
    );
  } catch {
    return null;
  }
}

type RawMarchTrafficEnvelope = {
  id?: string;
  traceId?: string;
  seq?: number;
  relative_ms?: number;
  timestamp?: string;
  channel?: string;
  event?: string;
  data?: Record<string, unknown>;
};

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function normalizeLatencyMs(data: Record<string, unknown>): number {
  return (
    finiteNumber(data.processing_time_ms)
    ?? finiteNumber(data.duration_ms)
    ?? finiteNumber(data.latency_ms)
    ?? finiteNumber(data.latencyMs)
    ?? 0
  );
}

const HIDDEN_MARCH_DEBUG_EVENT_NAMES = new Set([
  'thinking.step_timing',
  'planner.status_update',
  'orchestrator.update',
  'stream.token',
  'stream.end',
  'stream.error',
  'response.metadata',
]);

export function isVisibleTrafficInspectorEvent(eventName?: string | null): boolean {
  const name = (eventName ?? '').trim();
  if (!name) return true;
  return !HIDDEN_MARCH_DEBUG_EVENT_NAMES.has(name);
}

function normalizeHistoricalTrafficEvent(item: unknown): TrafficInspectorEvent | null {
  if (!item || typeof item !== 'object') return null;
  const raw = item as RawMarchTrafficEnvelope & Partial<TrafficInspectorEvent>;
  if (typeof raw.source === 'string' && typeof raw.destination === 'string') {
    return {
      ...(raw as TrafficInspectorEvent),
      seq: finiteNumber(raw.seq),
      relativeMs: finiteNumber(raw.relative_ms),
      latencyMs: finiteNumber(raw.latencyMs) ?? 0,
    };
  }
  if (typeof raw.event !== 'string') return null;
  const runId = typeof raw.traceId === 'string' && raw.traceId ? raw.traceId : 'historical-trace';
  const data = raw.data && typeof raw.data === 'object' ? { ...raw.data } : {};
  if (typeof raw.timestamp === 'string' && raw.timestamp && typeof data.timestamp !== 'string') {
    data.timestamp = raw.timestamp;
  }
  if (finiteNumber(raw.relative_ms) != null && finiteNumber(data.relative_ms) == null) {
    data.relative_ms = finiteNumber(raw.relative_ms);
  }
  if (finiteNumber(raw.seq) != null && finiteNumber(data.seq) == null) {
    data.seq = finiteNumber(raw.seq);
  }
  return marchSseToTrafficEvent(
    {
      channel: typeof raw.channel === 'string' ? raw.channel : 'thinking',
      event: raw.event,
      data,
      id: typeof raw.seq === 'number' ? raw.seq : undefined,
    },
    runId,
    { next: typeof raw.seq === 'number' && raw.seq > 0 ? raw.seq - 1 : 0 }
  );
}

export function normalizeTrafficStreamEventPayload(item: unknown): TrafficInspectorEvent | null {
  return normalizeHistoricalTrafficEvent(item);
}

type TrafficEventQueryParams = {
  from?: string;
  to?: string;
  filter?: string;
  limit?: number;
};

async function fetchTrafficEvents(params: TrafficEventQueryParams): Promise<TrafficInspectorEvent[]> {
  const sp = new URLSearchParams();
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.filter) sp.set('filter', params.filter);
  if (params.limit != null) sp.set('limit', String(params.limit));
  const q = sp.toString();
  const list = await adminFetch<unknown[]>(`/v1/admin/traffic/events${q ? `?${q}` : ''}`);
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => normalizeHistoricalTrafficEvent(item))
    .filter((item): item is TrafficInspectorEvent => item != null);
}

/** Historical events for fallback paths that need retry/backoff on REST failure. */
export async function getTrafficEventsStrict(params: TrafficEventQueryParams): Promise<TrafficInspectorEvent[]> {
  return fetchTrafficEvents(params);
}

/** Historical events (when backend implements GET /v1/admin/traffic/events). */
export async function getTrafficEvents(params: TrafficEventQueryParams): Promise<TrafficInspectorEvent[]> {
  try {
    return await fetchTrafficEvents(params);
  } catch {
    return [];
  }
}

// --- Fallback topology (from existing health/tools API when stream is not available) ---

export async function getFallbackTopology(): Promise<{
  nodes: TrafficInspectorNode[];
  edges: TrafficInspectorEdge[];
}> {
  if (getTrafficInspectorMode() !== 'command') {
    return getMarchFallbackTopology();
  }
  const nodes: TrafficInspectorNode[] = [];
  const edges: TrafficInspectorEdge[] = [];
  try {
    const [health, actuator] = await Promise.all([
      adminFetch<{ status?: string; commandApi?: boolean }>('/v1/health').catch(() => null),
      adminFetch<{
        status?: string;
        components?: { db?: { status?: string }; mcp?: { status?: string; details?: Record<string, unknown> } };
      }>('/actuator/health').catch(() => null),
    ]);
    const cmdUp = health?.status === 'up' || health?.commandApi === true;
    const dbStatus = actuator?.components?.db?.status ?? (actuator ? 'UP' : null);
    const mcpDetail = actuator?.components?.mcp?.details?.mcp ?? '';
    const mcpUp = actuator?.components?.mcp?.status === 'UP';
    const node = (
      id: string,
      label: string,
      type: TrafficInspectorNode['type'],
      health: TrafficInspectorNode['health'],
      tier: number
    ): TrafficInspectorNode => ({ id, label, type, health, tier });
    nodes.push(node('command-api', 'Command API', 'api', cmdUp ? 'UP' : 'DOWN', 1));
    nodes.push(node('database', 'Database', 'database', dbStatus === 'UP' ? 'UP' : actuator ? 'DOWN' : 'DEGRADED', 5));
    nodes.push(
      node(
        'mcp',
        'MCP / Agents',
        'agent',
        mcpUp ? 'UP' : mcpDetail === 'disabled' ? 'DEGRADED' : actuator ? 'DOWN' : 'DEGRADED',
        4
      )
    );
    nodes.push(node('rag', 'RAG', 'service', 'DEGRADED', 4));
    nodes.push(node('commander', 'Commander', 'service', cmdUp ? 'UP' : 'DOWN', 2));
    nodes.push(node('orchestrator', 'Orchestrator', 'service', cmdUp ? 'UP' : 'DOWN', 3));
    nodes.push(node('memory', 'Memory (T/P blobs)', 'service', cmdUp ? 'UP' : 'DOWN', 4));
    nodes.push(node('audit', 'Audit', 'service', cmdUp ? 'UP' : 'DOWN', 4));

    const edge = (source: string, target: string, rps: number, latencyMs: number, errorRate: number): TrafficInspectorEdge => ({
      source,
      target,
      requestRatePerSec: rps,
      avgLatencyMs: latencyMs,
      errorRate,
    });
    edges.push(edge('command-api', 'commander', 1, 50, 0));
    edges.push(edge('commander', 'orchestrator', 1, 30, 0));
    edges.push(edge('commander', 'memory', 0.5, 5, 0));
    edges.push(edge('memory', 'commander', 0.5, 5, 0));
    edges.push(edge('orchestrator', 'memory', 0.5, 10, 0));
    edges.push(edge('orchestrator', 'audit', 1, 20, 0));
    edges.push(edge('orchestrator', 'mcp', 0.5, 100, 0));
    edges.push(edge('memory', 'database', 0.2, 15, 0));
    edges.push(edge('audit', 'database', 0.2, 15, 0));
    edges.push(edge('orchestrator', 'rag', 0, 0, 0));
  } catch {
    // return empty
  }
  return { nodes, edges };
}

async function getMarchFallbackTopology(): Promise<{
  nodes: TrafficInspectorNode[];
  edges: TrafficInspectorEdge[];
}> {
  const base = getBase();
  if (!isMarchHttpApiConfigured()) {
    return buildMarchTopology({ [MARCH_NODE_IDS.api]: 'DOWN' });
  }
  // Prefer full topology from backend (nodes + edges + health)
  try {
    const res = await fetchWithAuth(`${base}/v1/admin/topology`, {
      method: 'GET',
      extraHeaders: adminHeaders(),
    });
    if (res.ok) {
      const body = await safeJson(res) as { nodes?: TrafficInspectorNode[]; edges?: TrafficInspectorEdge[] };
      const candidate = { nodes: body.nodes, edges: body.edges };
      if (isValidTopology(candidate)) {
        return candidate;
      }
    }
  } catch {
    // fall through to health-only fallback
  }
  // Fallback: fetch health only and build topology client-side
  let healthMap: NodeHealthMap | undefined;
  try {
    const res = await fetchWithAuth(`${base}/v1/admin/topology/health`, {
      method: 'GET',
      extraHeaders: adminHeaders(),
    });
    if (res.ok) {
      const body = await safeJson(res) as { components?: Record<string, string> };
      healthMap = body.components as NodeHealthMap | undefined;
    }
  } catch {
    // Fallback: check /health for March API at least
    try {
      const res = await fetch(`${base}/health`, { method: 'GET' });
      healthMap = res.ok ? { 'march-api': 'UP' } : { 'march-api': 'DOWN' };
    } catch {
      healthMap = { 'march-api': 'DOWN' };
    }
  }
  return buildMarchTopology(healthMap);
}

/** Simple non-crypto hash for patient_id; last 8 chars only, never full PHI. */
function patientIdHashLast8(patientId: string): string {
  if (!patientId || typeof patientId !== 'string') return '';
  let h = 0;
  for (let i = 0; i < patientId.length; i++) {
    h = (h << 5) - h + patientId.charCodeAt(i);
    h = h & h;
  }
  const hex = Math.abs(h).toString(16);
  return hex.slice(-8);
}

function humanizeIdentifier(value: string): string {
  return value
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function payloadPreview(value: unknown, limit: number = 600): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'string') {
    return value.length > limit ? `${value.slice(0, limit - 1)}…` : value;
  }
  try {
    const json = JSON.stringify(value, null, 2);
    if (!json) return undefined;
    return json.length > limit ? `${json.slice(0, limit - 1)}…` : json;
  } catch {
    return String(value);
  }
}

function payloadSizeBytes(value: unknown): number | undefined {
  if (value == null) return undefined;
  try {
    const encoded = new TextEncoder().encode(
      typeof value === 'string' ? value : JSON.stringify(value)
    );
    return encoded.byteLength;
  } catch {
    return undefined;
  }
}

function inferNodeId(value: string | undefined): string | null {
  const raw = (value ?? '').toString().trim().toLowerCase();
  if (!raw) return null;
  if (raw.startsWith('specialty:') || raw.startsWith('specialty-rag:') || raw === 'specialty-router' || raw === 'specialty-synthesis') {
    return raw;
  }
  if (raw.includes('guardrail')) return MARCH_NODE_IDS.guardrail;
  if (raw.includes('interpreter')) return MARCH_NODE_IDS.interpreter;
  if (raw.includes('context')) return MARCH_NODE_IDS.contextSummarizer;
  if (raw.includes('planner')) return MARCH_NODE_IDS.planner;
  if (raw.includes('orchestrator')) return MARCH_NODE_IDS.orchestrator;
  if (raw.includes('clinical_quality') || raw.includes('clinical-quality')) return MARCH_NODE_IDS.clinicalQuality;
  if (raw.includes('multiagent') || raw.includes('multi-agent')) return MARCH_NODE_IDS.multiAgentPlatform;
  if (raw.includes('tool')) return MARCH_NODE_IDS.tools;
  if (raw.includes('mcp')) return MARCH_NODE_IDS.mcp;
  if (raw.includes('rag')) return MARCH_NODE_IDS.rag;
  if (raw.includes('sql')) return MARCH_NODE_IDS.sqlDb;
  if (raw.includes('digital') || raw.includes('twin')) return MARCH_NODE_IDS.digitalTwin;
  if (raw.includes('risk')) return MARCH_NODE_IDS.riskHeartbeat;
  if (raw.includes('response')) return MARCH_NODE_IDS.responseHub;
  if (raw.includes('stream') || raw.includes('sse')) return MARCH_NODE_IDS.sse;
  if (raw.includes('api')) return MARCH_NODE_IDS.api;
  if (raw.includes('client')) return MARCH_NODE_IDS.client;
  return null;
}

function specialtyNodeId(rawSpecialty: unknown, ragCorpus?: unknown): string {
  const specialty = String(rawSpecialty ?? '').trim().toLowerCase();
  const corpus = String(ragCorpus ?? '').trim().toLowerCase();
  if (corpus) return `specialty-rag:${corpus}`;
  if (specialty) return `specialty:${specialty}`;
  return 'specialty:unknown';
}

function specialtyAgentNodeId(rawSpecialty: unknown): string {
  const specialty = String(rawSpecialty ?? '').trim().toLowerCase();
  return specialty ? `specialty:${specialty}` : 'specialty:unknown';
}

function inferToolServiceNode(
  toolIdRaw: unknown,
  toolCategoryRaw: unknown
): string {
  const toolId = String(toolIdRaw ?? '').trim().toLowerCase();
  const toolCategory = String(toolCategoryRaw ?? '').trim().toLowerCase();
  if (toolId === 'get_patient_context' || toolId === 'get_twin_snapshot') return MARCH_NODE_IDS.digitalTwin;
  if (toolId === 'get_patient_summary') {
    return MARCH_NODE_IDS.digitalTwin;
  }
  if (toolCategory === 'rag' || toolId === 'query_clinical_evidence') return MARCH_NODE_IDS.rag;
  if (toolCategory === 'terminology' || toolId.includes('umls')) return MARCH_NODE_IDS.mcp;
  if (toolCategory === 'duckdb' || toolId.startsWith('get_') || toolId.includes('list_') || toolId.includes('download_')) {
    return MARCH_NODE_IDS.sqlDb;
  }
  if (toolCategory === 'mcp' || toolId === 'get_patient_context' || toolId.startsWith('run_export')) return MARCH_NODE_IDS.mcp;
  if (toolCategory === 'external_agent' || toolId === 'run_simulation' || toolId === 'get_draft_note' || toolId === 'get_draft_plan' || toolId === 'revise_note') {
    return MARCH_NODE_IDS.multiAgentPlatform;
  }
  if (toolCategory === 'ehr_write' || toolId.startsWith('save_') || toolId.startsWith('update_') || toolId.startsWith('create_') || toolId.startsWith('resolve_') || toolId.startsWith('escalate_') || toolId.startsWith('ack_')) {
    return MARCH_NODE_IDS.api;
  }
  return MARCH_NODE_IDS.tools;
}

function normalizeEventStatus(
  raw: unknown,
  fallback: 'ok' | 'error' | 'timeout' | 'pending' = 'ok'
): 'ok' | 'error' | 'timeout' | 'pending' {
  const value = String(raw ?? '').trim().toLowerCase();
  if (!value) return fallback;
  if (value === 'pending' || value === 'started' || value === 'running') return 'pending';
  if (value === 'timeout' || value === 'timed_out') return 'timeout';
  if (value === 'error' || value === 'failed' || value === 'failure' || value === 'blocked') return 'error';
  if (value === 'ok' || value === 'success' || value === 'completed' || value === 'done') return 'ok';
  return fallback;
}

function statusCodeFor(status: 'ok' | 'error' | 'timeout' | 'pending'): number {
  if (status === 'pending') return 102;
  if (status === 'timeout') return 504;
  if (status === 'error') return 500;
  return 200;
}

function routeThinkingStep(
  stepKey: string,
  data: Record<string, unknown>
): Pick<TrafficInspectorEvent, 'source' | 'destination' | 'messageType' | 'endpoint' | 'method' | 'summary' | 'tool'> {
  const label = typeof data.label === 'string' ? data.label : humanizeIdentifier(stepKey);
  const stepStatus = normalizeEventStatus(data.status, 'pending');
  if (stepKey === 'session.started') {
    return {
      source: MARCH_NODE_IDS.client,
      destination: MARCH_NODE_IDS.api,
      messageType: 'api_call',
      endpoint: '/api/v1/chat/message',
      method: 'POST',
      summary: label,
    };
  }
  if (stepKey.startsWith('guardrail.')) {
    if (stepKey.includes('output')) {
      return {
        source: stepStatus === 'pending' ? MARCH_NODE_IDS.interpreter : MARCH_NODE_IDS.guardrail,
        destination: stepStatus === 'pending' ? MARCH_NODE_IDS.guardrail : MARCH_NODE_IDS.api,
        messageType: 'internal_request',
        endpoint: stepKey,
        method: stepStatus === 'pending' ? 'CHECK' : 'RETURN',
        summary: label,
      };
    }
    return {
      source: stepStatus === 'pending' ? MARCH_NODE_IDS.api : MARCH_NODE_IDS.guardrail,
      destination: stepStatus === 'pending' ? MARCH_NODE_IDS.guardrail : MARCH_NODE_IDS.interpreter,
      messageType: 'internal_request',
      endpoint: stepKey,
      method: stepStatus === 'pending' ? 'CHECK' : 'RETURN',
      summary: label,
    };
  }
  if (stepKey.startsWith('interpreter.')) {
    return {
      source: MARCH_NODE_IDS.guardrail,
      destination: MARCH_NODE_IDS.interpreter,
      messageType: 'internal_request',
      endpoint: stepKey,
      method: 'PROCESS',
      summary: label,
    };
  }
  if (stepKey === 'pipeline.patient_history') {
    return {
      source: stepStatus === 'pending' ? MARCH_NODE_IDS.interpreter : MARCH_NODE_IDS.contextSummarizer,
      destination: stepStatus === 'pending' ? MARCH_NODE_IDS.contextSummarizer : MARCH_NODE_IDS.interpreter,
      messageType: 'internal_request',
      endpoint: stepKey,
      method: stepStatus === 'pending' ? 'LOAD' : 'RETURN',
      summary: label,
    };
  }
  if (stepKey === 'planner.build_plan') {
    return {
      source: MARCH_NODE_IDS.interpreter,
      destination: MARCH_NODE_IDS.planner,
      messageType: 'internal_request',
      endpoint: stepKey,
      method: 'PLAN',
      summary: label,
    };
  }
  if (stepKey.startsWith('tool.')) {
    const toolId = stepKey.replace(/^tool\./, '') || 'unknown';
    const stepStatus = normalizeEventStatus(data.status, 'pending');
    return {
      source: stepStatus === 'pending' ? MARCH_NODE_IDS.orchestrator : MARCH_NODE_IDS.tools,
      destination: stepStatus === 'pending' ? MARCH_NODE_IDS.tools : MARCH_NODE_IDS.orchestrator,
      messageType: stepStatus === 'pending' ? 'tool_call' : 'tool_result',
      endpoint: toolId,
      method: stepStatus === 'pending' ? 'CALL' : 'RESULT',
      summary: label,
      tool: toolId,
    };
  }
  const inferredSource = inferNodeId(stepKey.split('.')[0]);
  if (stepKey.startsWith('planner.')) {
    return {
      source: inferredSource ?? MARCH_NODE_IDS.planner,
      destination: MARCH_NODE_IDS.orchestrator,
      messageType: 'internal_request',
      endpoint: stepKey,
      method: 'PLAN',
      summary: label,
    };
  }
  return {
    source: inferredSource ?? MARCH_NODE_IDS.responseHub,
    destination: MARCH_NODE_IDS.responseHub,
    messageType: 'status_update',
    endpoint: stepKey,
    method: 'EVENT',
    summary: label,
  };
}

/**
 * Convert March chat SSE payload to TrafficInspectorEvent for the Request stream.
 * Maps pipeline steps (guardrail, interpreter, planner, orchestrator, tools) to source/dest.
 */
export function marchSseToTrafficEvent(
  payload: { channel?: string; event?: string; data?: Record<string, unknown>; id?: number },
  runId: string,
  spanSeq: { next: number }
): TrafficInspectorEvent | null {
  const eventName = (payload?.event ?? '').toString();
  const channel = (payload?.channel ?? '').toString() || undefined;
  const data = (payload?.data ?? {}) as Record<string, unknown>;
  if (!eventName) return null;

  const patientId = (data.patient_id as string) ?? (data.patientId as string) ?? '';
  const patientIdHash = patientId ? patientIdHashLast8(patientId) : undefined;
  // Debug-channel events (stream tokens, step timings, response metadata) are
  // NOT dropped: they carry the request's true end timestamp and the streamed
  // final answer (live-UAT task #62: CMA traces "ended" at the last chart tool
  // and resolved a tool name as the final response). They are marked hidden
  // and excluded from step/event listings at display time instead.
  const hidden = !isVisibleTrafficInspectorEvent(eventName);

  const spanId = payload?.id != null ? `sse-${payload.id}` : `span-${spanSeq.next++}`;
  const timestamp = typeof data.timestamp === 'string' ? data.timestamp : new Date().toISOString();
  const preview = payloadPreview(data);
  const eventStatus = normalizeEventStatus(data.status, 'ok');
  const traceId =
    (typeof data.request_id === 'string' && data.request_id.trim())
    || (typeof data.requestId === 'string' && data.requestId.trim())
    || (typeof data.correlation_id === 'string' && data.correlation_id.trim())
    || (typeof data.correlationId === 'string' && data.correlationId.trim())
    || (typeof data.trace_id === 'string' && data.trace_id.trim())
    || (typeof data.traceId === 'string' && data.traceId.trim())
    || runId;

  const base: Partial<TrafficInspectorEvent> = {
    id: `${traceId}:${spanId}`,
    traceId,
    spanId,
    seq: finiteNumber(payload?.id),
    relativeMs: finiteNumber(data.relative_ms),
    timestamp,
    protocol: channel === 'final' ? 'WS' : 'Internal',
    eventName,
    channel,
    status: eventStatus,
    statusCode: statusCodeFor(eventStatus),
    latencyMs: normalizeLatencyMs(data),
    payload: data,
    payloadPreview: preview,
    ...(hidden ? { hidden: true } : {}),
  };

  if (eventName === 'session.started') {
    return {
      ...base,
      source: MARCH_NODE_IDS.client,
      destination: MARCH_NODE_IDS.api,
      messageType: 'api_call',
      endpoint: '/chat',
      method: 'POST',
      summary: 'Client request started',
      requestSizeBytes: payloadSizeBytes(data),
      patientIdHash,
    } as TrafficInspectorEvent;
  }
  if (eventName === 'guardrail.input.checked' || eventName === 'guardrail.output.checked') {
    const status = normalizeEventStatus(
      typeof data.passed === 'boolean' ? (data.passed ? 'success' : 'failed') : data.status,
      'ok'
    );
    return {
      ...base,
      source: eventName.includes('input') ? MARCH_NODE_IDS.api : MARCH_NODE_IDS.interpreter,
      destination: MARCH_NODE_IDS.guardrail,
      messageType: 'internal_request',
      endpoint: eventName,
      method: 'CHECK',
      status,
      statusCode: statusCodeFor(status),
      latencyMs: normalizeLatencyMs(data),
      summary: typeof data.passed === 'boolean'
        ? `${eventName.includes('input') ? 'Inbound' : 'Outbound'} guardrail ${data.passed ? 'passed' : 'blocked'}`
        : humanizeIdentifier(eventName),
      requestSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'thinking.step_timing') {
    const stepKey = typeof data.step_key === 'string' ? data.step_key : eventName;
    const route = routeThinkingStep(stepKey, data);
    const status = normalizeEventStatus(data.status, 'pending');
    const duration = normalizeLatencyMs(data);
    return {
      ...base,
      ...route,
      status,
      statusCode: statusCodeFor(status),
      latencyMs: duration,
      requestSizeBytes: status === 'pending' ? payloadSizeBytes(data) : undefined,
      responseSizeBytes: status === 'pending' ? undefined : payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'planner.plan_ready') {
    return {
      ...base,
      source: MARCH_NODE_IDS.planner,
      destination: MARCH_NODE_IDS.orchestrator,
      messageType: 'response',
      endpoint: 'plan_ready',
      method: 'PLAN',
      summary: typeof data.step_count === 'number'
        ? `Execution plan ready with ${data.step_count} step${data.step_count === 1 ? '' : 's'}`
        : 'Execution plan ready',
      responseSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'planner.status_update') {
    return {
      ...base,
      source: MARCH_NODE_IDS.planner,
      destination: MARCH_NODE_IDS.responseHub,
      messageType: 'status_update',
      endpoint: typeof data.step_name === 'string' ? data.step_name : eventName,
      method: 'STATUS',
      status: normalizeEventStatus(data.status, 'pending'),
      statusCode: statusCodeFor(normalizeEventStatus(data.status, 'pending')),
      latencyMs: normalizeLatencyMs(data),
      summary: typeof data.message === 'string' ? data.message : humanizeIdentifier(eventName),
      responseSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'orchestrator.update') {
    const rawSource = (data.source as string) ?? MARCH_NODE_IDS.orchestrator;
    const source = inferNodeId(rawSource) ?? MARCH_NODE_IDS.orchestrator;
    const status = normalizeEventStatus(data.status, 'pending');
    return {
      ...base,
      source,
      destination: MARCH_NODE_IDS.responseHub,
      messageType: 'status_update',
      endpoint: rawSource,
      method: 'STATUS',
      status,
      statusCode: statusCodeFor(status),
      latencyMs: normalizeLatencyMs(data),
      summary: typeof data.message === 'string' ? data.message : humanizeIdentifier(rawSource),
      responseSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'orchestrator.tool_called') {
    const toolId = (data.tool_id as string) ?? 'unknown';
    const destination = inferToolServiceNode(toolId, data.tool_category);
    return {
      ...base,
      source: MARCH_NODE_IDS.orchestrator,
      destination,
      messageType: 'tool_call',
      endpoint: toolId,
      method: 'CALL',
      status: 'pending',
      statusCode: statusCodeFor('pending'),
      summary: typeof data.display_message === 'string' ? data.display_message : `Calling ${humanizeIdentifier(toolId)}`,
      requestSizeBytes: payloadSizeBytes(data),
      tool: toolId,
    } as TrafficInspectorEvent;
  }
  if (eventName === 'orchestrator.tool_result') {
    const toolId = (data.tool_id as string) ?? 'unknown';
    const status = (data.status as string) ?? 'success';
    const processingTimeMs = normalizeLatencyMs(data);
    const source = inferToolServiceNode(toolId, data.tool_category);
    return {
      ...base,
      source,
      destination: MARCH_NODE_IDS.orchestrator,
      messageType: 'tool_result',
      endpoint: toolId,
      method: 'RESULT',
      status: status === 'success' ? 'ok' : 'error',
      statusCode: status === 'success' ? 200 : 500,
      latencyMs: processingTimeMs,
      summary: typeof data.display_message === 'string'
        ? data.display_message
        : `${humanizeIdentifier(toolId)} ${status === 'success' ? 'completed' : 'failed'}`,
      responseSizeBytes: payloadSizeBytes(data),
      tool: toolId,
    } as TrafficInspectorEvent;
  }
  if (eventName === 'multiagent.routing') {
    const specialties = Array.isArray(data.specialties) ? data.specialties.map(String) : [];
    return {
      ...base,
      source: MARCH_NODE_IDS.orchestrator,
      destination: 'specialty-router',
      messageType: 'internal_request',
      endpoint: 'multiagent.routing',
      method: 'ROUTE',
      summary: specialties.length > 0
        ? `Routing to ${specialties.length} specialty lane${specialties.length === 1 ? '' : 's'}`
        : 'Multi-agent routing',
      requestSizeBytes: payloadSizeBytes(data),
      isParallel: Boolean(data.parallel),
      parallelGroup: typeof data.parallel_group === 'string' ? data.parallel_group : undefined,
    } as TrafficInspectorEvent;
  }
  if (eventName === 'multiagent.specialtyagentcalled') {
    const specialty = typeof data.specialty === 'string' ? data.specialty : undefined;
    const destination = specialtyAgentNodeId(specialty);
    return {
      ...base,
      source: MARCH_NODE_IDS.multiAgentPlatform,
      destination,
      messageType: 'agent_call',
      endpoint: typeof data.agent_name === 'string' ? data.agent_name : eventName,
      method: 'CALL',
      status: 'pending',
      statusCode: statusCodeFor('pending'),
      summary: specialty
        ? `Dispatch to ${humanizeIdentifier(specialty)} specialty${data.rag_corpus ? ` via ${String(data.rag_corpus)}` : ''}`
        : 'Dispatch to specialty lane',
      requestSizeBytes: payloadSizeBytes(data),
      specialty,
      isParallel: true,
      parallelGroup: typeof data.parallel_group === 'string' ? data.parallel_group : 'specialty-fanout',
    } as TrafficInspectorEvent;
  }
  if (eventName === 'multiagent.specialty_result') {
    const specialty = typeof data.specialty === 'string' ? data.specialty : undefined;
    const source = specialtyAgentNodeId(specialty);
    const status = normalizeEventStatus(data.status, 'ok');
    return {
      ...base,
      source,
      destination: 'specialty-synthesis',
      messageType: 'agent_result',
      endpoint: typeof data.agent_name === 'string' ? data.agent_name : eventName,
      method: 'RESULT',
      status,
      statusCode: statusCodeFor(status),
      latencyMs: normalizeLatencyMs(data),
      summary: typeof data.summary === 'string' && data.summary.trim()
        ? data.summary
        : (specialty ? `${humanizeIdentifier(specialty)} specialty returned` : 'Specialty lane returned'),
      responseSizeBytes: payloadSizeBytes(data),
      specialty,
      isParallel: true,
      parallelGroup: typeof data.parallel_group === 'string' ? data.parallel_group : 'specialty-fanout',
    } as TrafficInspectorEvent;
  }
  if (eventName === 'multiagent.specialty_rag_lookup') {
    const specialty = typeof data.specialty === 'string' ? data.specialty : undefined;
    const status = normalizeEventStatus(data.status, 'pending');
    const started = status === 'pending';
    return {
      ...base,
      source: started ? specialtyAgentNodeId(specialty) : specialtyNodeId(specialty, data.rag_corpus),
      destination: started ? specialtyNodeId(specialty, data.rag_corpus) : specialtyAgentNodeId(specialty),
      messageType: started ? 'internal_request' : 'response',
      endpoint: 'multiagent.specialty_rag_lookup',
      method: 'RAG',
      status,
      statusCode: statusCodeFor(status),
      latencyMs: normalizeLatencyMs(data),
      summary: specialty
        ? `${humanizeIdentifier(specialty)} specialty RAG ${started ? 'request' : status === 'error' ? 'failed' : 'completed'}`
        : `Specialty RAG ${started ? 'request' : status === 'error' ? 'failed' : 'completed'}`,
      requestSizeBytes: started ? payloadSizeBytes(data) : undefined,
      responseSizeBytes: started ? undefined : payloadSizeBytes(data),
      specialty,
      isParallel: true,
      parallelGroup: typeof data.parallel_group === 'string' ? data.parallel_group : 'specialty-fanout',
    } as TrafficInspectorEvent;
  }
  if (eventName === 'multiagent.parallel_specialties_complete') {
    return {
      ...base,
      source: 'specialty-synthesis',
      destination: MARCH_NODE_IDS.multiAgentPlatform,
      messageType: 'response',
      endpoint: 'multiagent.parallel_specialties_complete',
      method: 'JOIN',
      latencyMs: normalizeLatencyMs(data),
      summary: typeof data.count === 'number'
        ? `${data.count} specialty lane${data.count === 1 ? '' : 's'} completed`
        : 'Parallel specialties completed',
      responseSizeBytes: payloadSizeBytes(data),
      isParallel: true,
      parallelGroup: typeof data.parallel_group === 'string' ? data.parallel_group : 'specialty-fanout',
    } as TrafficInspectorEvent;
  }
  if (eventName === 'multiagent.rag_complete') {
    return {
      ...base,
      source: MARCH_NODE_IDS.rag,
      destination: MARCH_NODE_IDS.multiAgentPlatform,
      messageType: 'response',
      endpoint: 'multiagent.rag_complete',
      method: 'RAG',
      summary: typeof data.source_count === 'number'
        ? `${data.source_count} RAG source${data.source_count === 1 ? '' : 's'} retrieved`
        : 'RAG retrieval complete',
      responseSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'multiagent.reasoning_complete') {
    return {
      ...base,
      source: MARCH_NODE_IDS.multiAgentPlatform,
      destination: MARCH_NODE_IDS.orchestrator,
      messageType: 'response',
      endpoint: 'multiagent.reasoning_complete',
      method: 'SYNTHESIZE',
      summary: typeof data.evidence_count === 'number'
        ? `Multi-agent reasoning complete with ${data.evidence_count} evidence item${data.evidence_count === 1 ? '' : 's'}`
        : 'Multi-agent reasoning complete',
      responseSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName.startsWith('planner.') || eventName === 'planner.status_update') {
    const stepName = (data.step_name as string) ?? eventName;
    return {
      ...base,
      source: MARCH_NODE_IDS.planner,
      destination: MARCH_NODE_IDS.responseHub,
      messageType: 'status_update',
      endpoint: stepName,
      method: 'STATUS',
      summary: typeof data.message === 'string' ? data.message : humanizeIdentifier(stepName),
      responseSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'stream.token') {
    const token = typeof data.token === 'string' ? data.token : '';
    return {
      ...base,
      source: MARCH_NODE_IDS.sse,
      destination: MARCH_NODE_IDS.client,
      messageType: 'stream',
      endpoint: '/stream',
      method: 'TOKEN',
      summary: 'Final response token batch',
      responseSizeBytes: payloadSizeBytes(token ? { token } : data),
      payload: data,
    } as TrafficInspectorEvent;
  }
  if (eventName === 'stream.end') {
    return {
      ...base,
      source: MARCH_NODE_IDS.sse,
      destination: MARCH_NODE_IDS.client,
      messageType: 'stream',
      endpoint: '/stream',
      method: 'GET',
      summary: 'Stream completed',
      responseSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'stream.error') {
    return {
      ...base,
      source: MARCH_NODE_IDS.sse,
      destination: MARCH_NODE_IDS.client,
      messageType: 'stream',
      endpoint: '/stream',
      method: 'ERROR',
      status: 'error',
      statusCode: 500,
      summary: typeof data.message === 'string' ? data.message : 'Stream error',
      responseSizeBytes: payloadSizeBytes(data),
    } as TrafficInspectorEvent;
  }
  if (eventName === 'response.metadata') {
    const flow = (data.execution_flow as string) ?? undefined;
    return {
      ...base,
      source: MARCH_NODE_IDS.sse,
      destination: MARCH_NODE_IDS.client,
      messageType: 'response',
      endpoint: 'response.metadata',
      method: 'METADATA',
      flow: flow === 'EEF' || flow === 'CARF' || flow === 'DCRF' ? flow : undefined,
      summary: typeof data.intent_type === 'string'
        ? `Final response metadata for ${humanizeIdentifier(data.intent_type)}`
        : 'Final response metadata',
      responseSizeBytes: payloadSizeBytes(data),
      patientIdHash,
    } as TrafficInspectorEvent;
  }
  // Generic: treat as planner/orchestrator internal
  return {
    ...base,
    source: inferNodeId(eventName.split('.')[0]) ?? MARCH_NODE_IDS.responseHub,
    destination: MARCH_NODE_IDS.responseHub,
    messageType: 'event',
    endpoint: eventName,
    method: 'EVENT',
    summary: humanizeIdentifier(eventName),
    responseSizeBytes: payloadSizeBytes(data),
  } as TrafficInspectorEvent;
}

// Optional helper for future: map March SSE events to topology edges.
export function mapMarchEventToEdge(eventName: string): { source: string; target: string } | null {
  const ev = (eventName || '').trim();
  if (!ev) return null;
  if (ev === 'session.started') return { source: MARCH_NODE_IDS.client, target: MARCH_NODE_IDS.api };
  if (ev === 'guardrail.input.checked') return { source: MARCH_NODE_IDS.api, target: MARCH_NODE_IDS.guardrail };
  if (ev === 'guardrail.output.checked') return { source: MARCH_NODE_IDS.interpreter, target: MARCH_NODE_IDS.guardrail };
  if (ev.startsWith('interpreter.')) return { source: MARCH_NODE_IDS.guardrail, target: MARCH_NODE_IDS.interpreter };
  if (ev.startsWith('planner.')) return { source: MARCH_NODE_IDS.planner, target: MARCH_NODE_IDS.orchestrator };
  if (ev === 'orchestrator.tool_called' || ev === 'orchestrator.tool_result') {
    return { source: MARCH_NODE_IDS.orchestrator, target: MARCH_NODE_IDS.tools };
  }
  if (ev.startsWith('stream.') || ev === 'response.metadata') {
    return { source: MARCH_NODE_IDS.sse, target: MARCH_NODE_IDS.client };
  }
  return null;
}
